import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { apiRequest } from "../helpers/api";
import AuthenticatedMedia from "../components/AuthenticatedMedia";
import { EmojiPickerInput, HashtagInput } from "../components/SocialTextInputs";
import { Can, useAccess } from "../components/AccessContext";
import { showError, showSuccess, showWarning } from "../components/ToastNotifications";
import { SkeletonForm, SkeletonRegion } from "../components/skeleton/Skeleton";
import PostPreview from "../components/postStudio/PostPreview";
import StudioDialog from "../components/postStudio/StudioDialog";
import StudioField from "../components/postStudio/StudioField";
import ImageDirectionFields from "../components/postStudio/ImageDirectionFields";
import { updatePostWithStatus } from "../components/postStudio/postStudioApi";
import supportedContentTypes from "../config/contentTypes";
import {
  captionLimits, hashtagsToList, hashtagsToText, isLocked, platformName, studioPlatforms, studioRoutes, toDateTimeInput
} from "../components/postStudio/postStudioUtils";
import { MediaPicker } from "./MediaLibrary";
import "../components/postStudio/PostStudio.css";

const routes = studioRoutes.AI;
const textFields = ["title", "description", "caption", "emojis", "hashtags", "cta"];
const fieldLabels = { title: "title", description: "description", caption: "caption", emojis: "emojis", hashtags: "hashtags", cta: "call to action" };

const draftFromPost = (post) => ({
  title: post.title || "",
  description: post.description || "",
  caption: post.caption || "",
  emojis: post.emojis || "",
  hashtags: hashtagsToText(post.hashtags),
  cta: post.cta || "",
  platform: post.platform || studioPlatforms[0],
  contentType: post.contentType || "Informational",
  imagePrompt: post.imagePrompt || post.topic || post.title || "",
  videoPrompt: post.videoPrompt || post.topic || post.title || "",
  media: post.media || [],
  status: ["DRAFT", "APPROVED", "SCHEDULED"].includes(post.status) ? post.status : "DRAFT",
  scheduledAt: toDateTimeInput(post.scheduledAt)
});

export const validateAiDraft = (draft) => {
  const errors = {};
  if (!draft.title.trim()) errors.title = "Add a title.";
  if (!draft.caption.trim()) errors.caption = "A caption is required.";
  else if (draft.caption.length > (captionLimits[draft.platform] || 6000)) errors.caption = `${platformName(draft.platform)} captions can be up to ${captionLimits[draft.platform]} characters.`;
  if (draft.description.length > 3000) errors.description = "Keep the description under 3,000 characters.";
  if (draft.cta.length > 500) errors.cta = "Keep the call to action under 500 characters.";
  if (hashtagsToList(draft.hashtags).length > 30) errors.hashtags = "Use 30 hashtags or fewer.";
  if (draft.status === "SCHEDULED") {
    const when = new Date(draft.scheduledAt);
    if (!draft.scheduledAt || Number.isNaN(when.getTime())) errors.scheduledAt = "Choose a date and time to schedule.";
    else if (when <= new Date()) errors.scheduledAt = "Choose a time in the future.";
  }
  return errors;
};

const AIPostEditor = () => {
  const { postId } = useParams();
  const navigate = useNavigate();
  const { can } = useAccess();
  const [original, setOriginal] = useState(null);
  const [draft, setDraft] = useState(null);
  const [capabilities, setCapabilities] = useState(null);
  const [account, setAccount] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [errors, setErrors] = useState({});
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [generating, setGenerating] = useState(null);
  const [instructions, setInstructions] = useState("");
  const [undo, setUndo] = useState({});
  const [imageAspectRatio, setImageAspectRatio] = useState("square");
  const [imageDirection, setImageDirection] = useState({ overlayText: "", products: [] });
  const [brandProducts, setBrandProducts] = useState([]);
  const [videoAspectRatio, setVideoAspectRatio] = useState("portrait");
  const [pickerOpen, setPickerOpen] = useState(false);
  const [confirm, setConfirm] = useState(null);

  const loadPost = useCallback(async () => {
    const result = await apiRequest(`/api/posts/${encodeURIComponent(postId)}`);
    setOriginal(result.post);
    return result.post;
  }, [postId]);

  useEffect(() => {
    let active = true;
    setLoading(true);
    Promise.all([
      loadPost(),
      apiRequest("/api/content-generation/preferences").catch(() => ({})),
      apiRequest("/api/social/accounts").catch(() => ({ accounts: [] }))
    ]).then(([post, preferences, accounts]) => {
      if (!active) return;
      setDraft(draftFromPost(post));
      setCapabilities(preferences.aiCapabilities || null);
      setBrandProducts(preferences.brandContext?.products || []);
      setAccount((accounts.accounts || []).find((item) => item.status === "CONNECTED" && item.platform === post.platform) || null);
    }).catch((error) => { if (active) setLoadError(error.message || "Unable to load this post."); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [loadPost]);

  const update = (changes) => {
    setDraft((current) => ({ ...current, ...changes }));
    setDirty(true);
    setErrors((current) => Object.fromEntries(Object.entries(current).filter(([field]) => !(field in changes))));
  };

  const previewPost = useMemo(() => draft && ({
    ...draft, hashtags: hashtagsToList(draft.hashtags),
    scheduledAt: draft.status === "SCHEDULED" && draft.scheduledAt ? new Date(draft.scheduledAt).toISOString() : original?.scheduledAt
  }), [draft, original]);

  const regenerate = async (fields) => {
    if (instructions.length > 300) { showWarning("Keep AI instructions under 300 characters."); return; }
    setGenerating(fields.length > 1 ? "all" : fields[0]);
    try {
      const result = await apiRequest(`/api/posts/${original.id}/ai/fields`, {
        method: "POST",
        body: JSON.stringify({ fields, instructions: instructions.trim() || undefined, draft: { ...draft, hashtags: hashtagsToList(draft.hashtags) } })
      });
      const suggestions = result.suggestions || {};
      const changes = Object.fromEntries(Object.entries(suggestions).map(([field, value]) => [field, field === "hashtags" ? hashtagsToText(value) : value]));
      setUndo((current) => ({ ...current, ...Object.fromEntries(Object.keys(changes).map((field) => [field, draft[field]])) }));
      update(changes);
      const updated = Object.keys(changes).map((field) => fieldLabels[field]);
      showSuccess(`New ${updated.join(", ")} suggested. Review it, then save.`);
    } catch (error) {
      showError(error.message || "AI regeneration failed.");
    } finally {
      setGenerating(null);
    }
  };

  const undoField = (field) => {
    update({ [field]: undo[field] });
    setUndo((current) => Object.fromEntries(Object.entries(current).filter(([key]) => key !== field)));
  };

  // Image and video generation save to the post on the server, so refresh media afterwards.
  const generateMedia = async (type) => {
    const prompt = (type === "image" ? draft.imagePrompt : draft.videoPrompt).trim();
    if (!prompt) { showWarning(`Describe the ${type} you want first.`); return; }
    setGenerating(type);
    try {
      await apiRequest(`/api/posts/${original.id}/${type}/generate`, {
        method: "POST",
        body: JSON.stringify(type === "image"
          ? { prompt, aspectRatio: imageAspectRatio, overlayText: imageDirection.overlayText.trim(), products: imageDirection.products }
          : { prompt, aspectRatio: videoAspectRatio })
      });
      const post = await loadPost();
      setDraft((current) => ({ ...current, media: post.media || [] }));
      showSuccess(type === "image" ? "New image generated and saved to this post." : "Video generated and saved to this post.");
    } catch (error) {
      showError(error.message || `${type === "image" ? "Image" : "Video"} generation failed.`);
    } finally {
      setGenerating(null);
    }
  };

  const save = async (event) => {
    event.preventDefault();
    const nextErrors = validateAiDraft(draft);
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length) { showWarning("Fix the highlighted fields before saving."); return; }
    setSaving(true);
    try {
      const body = {
        title: draft.title.trim(), description: draft.description.trim(), caption: draft.caption.trim(), emojis: draft.emojis.trim(),
        hashtags: hashtagsToList(draft.hashtags), cta: draft.cta.trim(), platform: draft.platform, contentType: draft.contentType,
        imagePrompt: draft.imagePrompt, videoPrompt: draft.videoPrompt, mediaIds: draft.media.map((item) => item.id)
      };
      await updatePostWithStatus(original, body, draft.status, draft.status === "SCHEDULED" ? new Date(draft.scheduledAt).toISOString() : null);
      setDirty(false);
      showSuccess(draft.status === "SCHEDULED" ? "AI post saved and scheduled." : "AI post saved.");
      navigate(routes.list);
    } catch (error) {
      showError(error.message || "The post could not be saved.");
    } finally {
      setSaving(false);
    }
  };

  const deletePost = async () => {
    setSaving(true);
    try {
      await apiRequest(`/api/posts/${original.id}`, { method: "DELETE" });
      showSuccess("Post deleted.");
      navigate(routes.list);
    } catch (error) {
      showError(error.message || "The post could not be deleted.");
      setSaving(false);
    }
  };

  if (loading) return <section className="studio-page"><SkeletonRegion label="Loading AI post"><SkeletonForm fields={8} columns={2} textarea /></SkeletonRegion></section>;
  if (loadError) return <section className="studio-page"><div className="studio-empty" role="alert"><span className="studio-empty-icon is-error"><i className="ti ti-alert-triangle" aria-hidden="true" /></span><h3>Post unavailable</h3><p>{loadError}</p><Link className="btn btn-outline-primary" to={routes.list}>Back to AI Post Studio</Link></div></section>;
  if (original.generationSource !== "AI") return <section className="studio-page"><div className="studio-empty"><span className="studio-empty-icon"><i className="ti ti-pencil" aria-hidden="true" /></span><h3>This is a manual post</h3><p>Manual posts are edited in Manual Post Studio and never use AI.</p><Link className="btn btn-primary" to={studioRoutes.MANUAL.edit(original.id)}>Open in Manual Post Studio</Link></div></section>;
  if (isLocked(original)) return <section className="studio-page"><div className="studio-empty"><span className="studio-empty-icon"><i className="ti ti-lock" aria-hidden="true" /></span><h3>This post has been published</h3><p>Published posts can't be edited.</p><Link className="btn btn-outline-primary" to={routes.list}>Back to AI Post Studio</Link></div></section>;

  const aiAllowed = can("user.create_content.create") && Boolean(capabilities?.text?.available);
  const canSchedule = can("user.posts.schedule", "user.content_calendar.schedule");
  const anyBusy = saving || Boolean(generating);
  const limit = captionLimits[draft.platform];

  const aiButton = (field) => aiAllowed ? <span className="studio-ai-actions">
    {undo[field] !== undefined && <button type="button" className="studio-text-button" onClick={() => undoField(field)}><i className="ti ti-arrow-back-up" aria-hidden="true" /> Undo</button>}
    <button type="button" className="studio-ai-button" disabled={anyBusy} onClick={() => regenerate([field])} aria-label={`Regenerate ${fieldLabels[field]} with AI`}>
      {generating === field ? <span className="studio-spinner is-dark" aria-hidden="true" /> : <i className="ti ti-sparkles" aria-hidden="true" />} Regenerate
    </button>
  </span> : null;

  return <section className="studio-page">
    <header className="studio-header">
      <div>
        <Link className="studio-back" to={routes.list} onClick={(event) => { if (dirty) { event.preventDefault(); setConfirm("leave"); } }}><i className="ti ti-arrow-left" aria-hidden="true" /> AI Post Studio</Link>
        <h1>Edit AI post</h1>
        <p>Edit anything by hand, or regenerate a single field with AI. Nothing is saved until you press Save.</p>
      </div>
      <Can permission="user.posts.delete"><button type="button" className="btn btn-light studio-danger-button" disabled={anyBusy} onClick={() => setConfirm("delete")}><i className="ti ti-trash" aria-hidden="true" /> Delete</button></Can>
    </header>

    {!capabilities?.text?.available && <div className="studio-banner is-warning" role="status"><i className="ti ti-plug-off" aria-hidden="true" /><div><strong>AI regeneration is unavailable</strong><p>Your administrator hasn't configured an AI text model. You can still edit this post manually.</p></div></div>}

    <form className="studio-editor" noValidate onSubmit={save}>
      <div className="studio-editor-main">
        {aiAllowed && <section className="studio-panel studio-ai-panel">
          <StudioField label="Guide the AI (optional)"><input className="form-control" maxLength={300} value={instructions} onChange={(event) => setInstructions(event.target.value)} placeholder="e.g. Make it shorter and more playful" /></StudioField>
          <button type="button" className="btn btn-outline-primary" disabled={anyBusy} onClick={() => regenerate(textFields)}>{generating === "all" ? <><span className="studio-spinner is-dark" aria-hidden="true" /> Regenerating…</> : <><i className="ti ti-wand" aria-hidden="true" /> Regenerate all text</>}</button>
        </section>}

        <section className="studio-panel">
          <h2 className="studio-panel-title">Content</h2>
          <div className="studio-form-grid">
            <StudioField label="Title" wide error={errors.title} action={aiButton("title")}><input className="form-control" maxLength={255} value={draft.title} onChange={(event) => update({ title: event.target.value })} /></StudioField>
            <StudioField label="Platform"><select className="form-select" value={draft.platform} onChange={(event) => update({ platform: event.target.value })}>{studioPlatforms.map((item) => <option key={item} value={item}>{platformName(item)}</option>)}</select></StudioField>
            <StudioField label="Content type"><select className="form-select" value={draft.contentType} onChange={(event) => update({ contentType: event.target.value })}>{supportedContentTypes.map((type) => <option key={type} value={type}>{type}</option>)}</select></StudioField>
            <StudioField label="Caption" wide error={errors.caption} action={aiButton("caption")} hintTone={limit && draft.caption.length > limit ? "error" : undefined} hint={`${draft.caption.length}/${limit || 6000} characters for ${platformName(draft.platform)}`}><textarea className="form-control" rows="6" maxLength={6000} value={draft.caption} onChange={(event) => update({ caption: event.target.value })} /></StudioField>
            <StudioField label="Description" wide error={errors.description} action={aiButton("description")}><textarea className="form-control" rows="3" maxLength={3000} value={draft.description} onChange={(event) => update({ description: event.target.value })} /></StudioField>
            <StudioField label="Emojis" action={aiButton("emojis")}><EmojiPickerInput id="ai-post-emojis" value={draft.emojis} maxLength={255} onChange={(emojis) => update({ emojis })} /></StudioField>
            <StudioField label="Hashtags" error={errors.hashtags} action={aiButton("hashtags")}><HashtagInput id="ai-post-hashtags" value={draft.hashtags} onChange={(hashtags) => update({ hashtags })} /></StudioField>
            <StudioField label="Call to action" wide error={errors.cta} action={aiButton("cta")}><input className="form-control" maxLength={500} value={draft.cta} onChange={(event) => update({ cta: event.target.value })} /></StudioField>
          </div>
        </section>

        <section className="studio-panel">
          <div className="studio-panel-head"><h2 className="studio-panel-title">Media</h2><Can permission="user.media_library.view"><button type="button" className="studio-text-button" onClick={() => setPickerOpen(true)}><i className="ti ti-photo" aria-hidden="true" /> Choose from Media Library</button></Can></div>
          {draft.media.length > 0 ? <ul className="studio-media-list">{draft.media.map((item, index) => <li key={item.id}>
            {item.mimeType?.startsWith("video/") ? <AuthenticatedMedia src={item.url} video muted /> : <AuthenticatedMedia src={item.url} alt="" />}
            <span>{item.name || `Media ${index + 1}`}</span>
            <button type="button" className="studio-icon-button" aria-label={`Remove ${item.name || `media ${index + 1}`}`} onClick={() => update({ media: draft.media.filter((entry) => entry.id !== item.id) })}><i className="ti ti-x" aria-hidden="true" /></button>
          </li>)}</ul> : <p className="studio-muted">No media attached.</p>}
          {can("user.create_content.create") && <div className="studio-form-grid studio-media-generate">
            <div className="studio-field is-wide"><label htmlFor="ai-image-prompt">Image prompt</label><textarea id="ai-image-prompt" className="form-control" rows="2" maxLength={4000} value={draft.imagePrompt} onChange={(event) => update({ imagePrompt: event.target.value })} />
              <div className="studio-inline-actions"><select className="form-select" aria-label="Image format" value={imageAspectRatio} onChange={(event) => setImageAspectRatio(event.target.value)}><option value="square">Square · 1:1</option><option value="portrait">Portrait · 9:16</option><option value="landscape">Landscape · 16:9</option></select>
                <button type="button" className="btn btn-outline-primary" disabled={anyBusy || !capabilities?.image?.available} title={capabilities?.image?.available ? undefined : "Image generation isn't configured by your administrator"} onClick={() => generateMedia("image")}>{generating === "image" ? <><span className="studio-spinner is-dark" aria-hidden="true" /> Generating…</> : <><i className="ti ti-photo-ai" aria-hidden="true" /> {original.imageUrl ? "Regenerate image" : "Generate image"}</>}</button></div>
            </div>
            <ImageDirectionFields idPrefix="ai-post-image" products={brandProducts} value={imageDirection} onChange={(changes) => setImageDirection((current) => ({ ...current, ...changes }))} />
            <div className="studio-field is-wide"><label htmlFor="ai-video-prompt">Video prompt</label><textarea id="ai-video-prompt" className="form-control" rows="2" maxLength={4000} value={draft.videoPrompt} onChange={(event) => update({ videoPrompt: event.target.value })} />
              <div className="studio-inline-actions"><select className="form-select" aria-label="Video format" value={videoAspectRatio} onChange={(event) => setVideoAspectRatio(event.target.value)}><option value="portrait">Portrait · 9:16</option><option value="landscape">Landscape · 16:9</option></select>
                <button type="button" className="btn btn-outline-primary" disabled={anyBusy || !capabilities?.video?.available} title={capabilities?.video?.available ? undefined : "Video generation isn't configured by your administrator"} onClick={() => generateMedia("video")}>{generating === "video" ? <><span className="studio-spinner is-dark" aria-hidden="true" /> Generating (can take minutes)…</> : <><i className="ti ti-video" aria-hidden="true" /> {original.videoUrl ? "Regenerate video" : "Generate video"}</>}</button></div>
            </div>
          </div>}
        </section>

        <section className="studio-panel">
          <h2 className="studio-panel-title">Status &amp; schedule</h2>
          <div className="studio-segmented" role="radiogroup" aria-label="Post status">
            {[["DRAFT", "Draft", "ti-file-pencil"], ["APPROVED", "Ready", "ti-circle-check"], ["SCHEDULED", "Schedule", "ti-calendar-time"]].map(([value, label, icon]) => (
              <label key={value} className={draft.status === value ? "is-active" : ""}>
                <input type="radio" name="ai-status" value={value} checked={draft.status === value} disabled={value === "SCHEDULED" && !canSchedule} onChange={() => update({ status: value })} />
                <i className={`ti ${icon}`} aria-hidden="true" /> {label}
              </label>
            ))}
          </div>
          {draft.status === "SCHEDULED" && <StudioField label="Publish date and time" error={errors.scheduledAt} hintTone="warning"
            hint={!account && <>No connected {platformName(draft.platform)} account. <Link to="/social-accounts">Connect one</Link> before the scheduled time.</>}>
            <input className="form-control" type="datetime-local" min={toDateTimeInput(new Date())} value={draft.scheduledAt} onChange={(event) => update({ scheduledAt: event.target.value })} />
          </StudioField>}
        </section>
      </div>

      <aside className="studio-editor-side">
        <section className="studio-panel is-sticky">
          <div className="studio-panel-head"><h2 className="studio-panel-title">Preview</h2><small>{platformName(draft.platform)}</small></div>
          <PostPreview post={previewPost} accountName={account?.accountName || account?.username} />
        </section>
      </aside>

      <footer className="studio-editor-footer">
        <button type="button" className="btn btn-light" disabled={saving} onClick={() => (dirty ? setConfirm("leave") : navigate(routes.list))}>Cancel</button>
        <Can permission="user.posts.edit"><button type="submit" className="btn btn-primary studio-primary-button" disabled={anyBusy}>{saving ? "Saving…" : draft.status === "SCHEDULED" ? "Save & schedule" : "Save changes"}</button></Can>
      </footer>
    </form>

    {pickerOpen && <MediaPicker initialSelected={draft.media} onClose={() => setPickerOpen(false)} onConfirm={(media) => {
      if (media.length > 10) { showWarning("A post can include up to 10 media items."); return; }
      update({ media });
      setPickerOpen(false);
    }} />}
    {confirm === "leave" && <StudioDialog role="alertdialog" eyebrow="UNSAVED CHANGES" title="Discard your changes?" onClose={() => setConfirm(null)}
      actions={<><button type="button" className="btn btn-light" onClick={() => setConfirm(null)}>Keep editing</button><button type="button" className="btn btn-danger" onClick={() => navigate(routes.list)}>Discard</button></>}>
      <p className="studio-dialog-copy">AI suggestions and edits that haven't been saved will be lost.</p>
    </StudioDialog>}
    {confirm === "delete" && <StudioDialog role="alertdialog" eyebrow="REMOVE POST" title="Delete this post?" busy={saving} onClose={() => setConfirm(null)}
      actions={<><button type="button" className="btn btn-light" disabled={saving} onClick={() => setConfirm(null)}>Cancel</button><button type="button" className="btn btn-danger" disabled={saving} onClick={deletePost}>{saving ? "Deleting…" : "Delete post"}</button></>}>
      <p className="studio-dialog-copy">This removes the post from AI Post Studio and the Content Calendar.</p>
    </StudioDialog>}
  </section>;
};

export default AIPostEditor;
